import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { ChevronUpDownIcon, DocumentTextIcon, XMarkIcon } from '@heroicons/react/20/solid';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { AgGridReact } from 'ag-grid-react';
import { format } from 'date-fns';
import DownloadSVG from '../../assets/images/icons/DownloadSVG';
import FilterSVG from 'src/assets/images/icons/FilterSVG';
import { exportToPDF } from '../../utils/agGridPdfExporter';
import { AGENT_ASSIST_EVALUATION, AGENT_ASSIST_EVALUATION_UPDATE } from '../../utils/apiUrls';
import { getAccessToken } from '../../utils/token-service';
import EditModal from './edit-modal';
import FilterSidebar from './filter-sidebar';
import TranscriptModal from './transcript-modal';
import JsonTreeDisplay, { buildTree, flattenTree, convertToCSV } from './json-tree-display';

interface ApiResponse {
	id: number;
	question: string;
	parent_intent: string;
	child_intent: string;
	llm_response: string;
	agent_response: string;
	similarity_score: number;
	matching_response: string;
	matching_reason: string;
	matching_short_reason: string;
	suggested_response: string;
	remarks: string;
	feedback_given_by: string;
	review_status: string;
	created_at: string;
	customer_details: string;
	transcript_link: string;
}

const LLMResponseTool: React.FC = () => {
	const { instance } = useMsal();
	const gridRef = useRef<AgGridReact>(null);
	const [loading, setLoading] = useState<boolean>(false);
	const [isInitialLoad, setIsInitialLoad] = useState(true);
	const [gridResponse, setGridResponse] = useState<ApiResponse[]>([]);
	const [error, setError] = useState<string | null>(null);
	const [startDateTime, setStartDateTime] = useState('');
	const [endDateTime, setEndDateTime] = useState('');
	const [selectedParentIntent, setSelectedParentIntent] = useState<string>('');
	const [selectedChildIntent, setSelectedChildIntent] = useState<string>('');
	const [parentIntents, setParentIntents] = useState<string[]>([]);
	const [childIntents, setChildIntents] = useState<string[]>([]);
	const [visibleColumns, setVisibleColumns] = useState<string[]>([
		'parent_intent',
		'child_intent',
		'question',
		'llm_response',
		'agent_response',
		'suggested_response',
		'remarks',
		'action',
	]);
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const [isSidebarOpen, setIsSidebarOpen] = useState(false);
	const [transcriptLink, setTranscriptLink] = useState<string | null>(null);
	const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
	const [transcriptModalOpen, setTranscriptModalOpen] = useState<boolean>(false);
	const [editableRowData, setEditableRowData] = useState<ApiResponse | null>(null);
	const [isJsonTreeModalOpen, setIsJsonTreeModalOpen] = useState<boolean>(false);

	const isCalled = useRef(false);

	const handleDownloadCSV = () => {
		const treeData = buildTree();
		const rows = flattenTree(treeData);
		const csvString = convertToCSV(rows);
		const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
		const url = URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = url;
		link.download = 'treeData.csv';
		link.click();
		URL.revokeObjectURL(url);
	};

	const fetchData = useCallback(async () => {
		if (isCalled.current) return;
		isCalled.current = true;

		if (!isInitialLoad && !startDateTime && !endDateTime && !selectedParentIntent && !selectedChildIntent) {
			setLoading(false);
			isCalled.current = false;
			return;
		}

		setLoading(true);
		setError(null);

		try {
			let accessToken = await getAccessToken();

			interface RequestBody {
				from_date?: string;
				to_date?: string;
				child_intent?: string;
				parent_intent?: string;
			}

			const requestBody: RequestBody = {};
			if (startDateTime) {
				requestBody.from_date = format(new Date(startDateTime), 'yyyy-MM-dd HH:mm:ss');
			}
			if (endDateTime) {
				requestBody.to_date = format(new Date(endDateTime), 'yyyy-MM-dd HH:mm:ss');
			}
			if (selectedChildIntent) {
				requestBody.child_intent = selectedChildIntent;
			}
			if (selectedParentIntent) {
				requestBody.parent_intent = selectedParentIntent;
			}

			const response = await fetch(AGENT_ASSIST_EVALUATION, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${accessToken}`,
				},
				body: JSON.stringify(requestBody),
			});

			if (!response.ok) {
				throw new Error(`HTTP error! Status: ${response.status}`);
			}

			const result: ApiResponse[] = await response.json();

			const sortedResult = (result || []).sort((a, b) => {
				if (a.review_status === 'Reviewed' && b.review_status !== 'Reviewed') return 1;
				if (a.review_status !== 'Reviewed' && b.review_status === 'Reviewed') return -1;
				return 0;
			});
			setGridResponse(sortedResult);

			if (parentIntents.length === 0) {
				const parentIntentList: string[] = result.map((item) => item.parent_intent);
				setParentIntents([...new Set(parentIntentList)]);
			}
			if (childIntents.length === 0) {
				const childIntentList: string[] = result.map((item) => item.child_intent);
				setChildIntents([...new Set(childIntentList)]);
			}
		} catch (err: any) {
			console.error('Error fetching data:', err);
			setError(err.message || 'An unexpected error occurred.');
		} finally {
			setIsInitialLoad(false);
			setLoading(false);
			isCalled.current = false;
		}
	}, [
		isInitialLoad,
		startDateTime,
		endDateTime,
		selectedParentIntent,
		selectedChildIntent,
		parentIntents.length,
		childIntents.length,
	]);

	useEffect(() => {
		fetchData();
	}, [fetchData]);

	const handleTranscriptClick = (link: string) => {
		setTranscriptLink(link);
		setTranscriptModalOpen(true);
	};

	const handleEditClick = (params: any) => {
		setEditableRowData({ ...params.data });
		setIsModalOpen(true);
	};

	const handleSave = async (updatedData: ApiResponse) => {
		const accessToken = await getAccessToken();
		try {
			const response = await fetch(`${AGENT_ASSIST_EVALUATION_UPDATE}`, {
				method: 'PUT',
				headers: {
					'Content-Type': 'application/json',
					Authorization: `Bearer ${accessToken}`,
				},
				body: JSON.stringify(updatedData),
			});
			if (!response.ok) {
				throw new Error(`HTTP error! Status: ${response.status}`);
			}
			await response.json();
			setIsInitialLoad(true);
			setGridResponse((prevData) => prevData.map((row) => (row.id === updatedData.id ? updatedData : row)));
			handleModalClose();
		} catch (error) {
			console.error('Error updating data:', error);
		}
	};

	const handleModalClose = () => {
		setIsModalOpen(false);
		setEditableRowData(null);
	};

	const colDefs = [
		{ field: 'id', headerName: 'ID' },
		{ field: 'parent_intent', headerName: 'Parent Intent' },
		{ field: 'child_intent', headerName: 'Child Intent' },
		{ field: 'question', headerName: 'Question' },
		{ field: 'llm_response', headerName: 'LLM Response' },
		{ field: 'agent_response', headerName: 'Agent Response' },
		{ field: 'similarity_score', headerName: 'Similarity Score' },
		{ field: 'matching_response', headerName: 'Matching Response' },
		{ field: 'matching_reason', headerName: 'Matching Reason' },
		{ field: 'matching_short_reason', headerName: 'Matching Short Reason' },
		{ field: 'customer_details', headerName: 'Customer Details' },
		{
			field: 'transcript_link',
			headerName: 'Transcript',
			cellRenderer: (params: any) => {
				const { value } = params;
				return value ? (
					<div className="text-center">
						<button
							onClick={() => handleTranscriptClick(value)}
							className="text-blue-400 flex items-center justify-center"
						>
							<DocumentTextIcon className="h-5 w-5 mr-2" aria-hidden="true" />
						</button>
					</div>
				) : null;
			},
		},
		{
			field: 'suggested_response',
			headerName: 'Suggested Response',
			editable: true,
		},
		{
			field: 'remarks',
			headerName: 'Remarks',
			editable: true,
		},
		{ field: 'feedback_given_by', headerName: 'Feedback Given By' },
		{
			field: 'review_status',
			headerName: 'Review Status',
			editable: true,
			cellEditor: 'agSelectCellEditor',
			cellEditorParams: {
				values: ['Not reviewed', 'Reviewed', 'Invalid'],
			},
			comparator: (valueA: string, valueB: string) => {
				if (valueA === 'Reviewed' && valueB !== 'Reviewed') return 1;
				if (valueA !== 'Reviewed' && valueB === 'Reviewed') return -1;
				if (valueA > valueB) return 1;
				if (valueA < valueB) return -1;
				return 0;
			},
		},
		{
			field: 'created_at',
			headerName: 'Created At',
			valueFormatter: (params: any) => {
				const date = new Date(params.value);
				return date.toLocaleString();
			},
		},
		{
			field: 'action',
			headerName: 'Actions',
			cellRenderer: (params: any) => {
				return (
					<div className="flex items-center gap-2 text-white">
						<button
							className="flex items-center justify-between px-2 py-1 leading-4 bg-sky-600 shadow-sm rounded"
							onClick={() => handleEditClick(params)}
						>
							Edit
						</button>
					</div>
				);
			},
			editable: false,
		},
	];

	const defaultColDef = useMemo(
		() => ({
			sortable: true,
			filter: true,
			wrapText: true,
			autoHeight: true,
			cellStyle: { lineHeight: '20px', padding: '8px 14px', wordWrap: 'break-word' },
		}),
		[]
	);

	const toggleSidebar = () => {
		setIsSidebarOpen((prev) => !prev);
	};

	const toggleColumnVisibility = (field: string) => {
		setVisibleColumns((prevColumns) => {
			if (prevColumns.includes(field)) {
				return prevColumns.filter((col) => col !== field);
			} else {
				return [...prevColumns, field];
			}
		});
	};

	const onBtnExport = useCallback(() => {
		gridRef.current?.api.exportDataAsCsv();
	}, []);

	const handleExportPDF = () => {
		const filename = 'exported-data.pdf';
		if (gridRef.current?.api) {
			exportToPDF(gridRef.current.api, filename);
		} else {
			console.error('Failed to access grid API');
		}
	};

	const dropdownRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		const handleOutsideClick = (event: MouseEvent) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
				setIsDropdownOpen(false);
			}
		};

		document.addEventListener('click', handleOutsideClick);
		return () => {
			document.removeEventListener('click', handleOutsideClick);
		};
	}, []);

	return (
		<div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
			<h1 className="text-lg font-semibold leading-6 mb-4 text-gray-900">Agent Assist Evaluation</h1>
			<div className="flex justify-between items-end">
				<div className="w-64 relative" ref={dropdownRef}>
					<div
						className="dropdown-toggle w-full p-1 text-[14px] border border-gray-300 rounded-md cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-400"
						onClick={() => setIsDropdownOpen((prev) => !prev)}
					>
						Select Columns
						<span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
							<ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
						</span>
					</div>

					{isDropdownOpen && (
						<div className="absolute right-0 z-10 bg-white border rounded-md shadow-lg w-full mt-1 max-h-80 overflow-y-auto">
							{colDefs.map((col) => (
								<div key={col.field} className="flex items-center p-2 cursor-pointer hover:bg-gray-100">
									<input
										type="checkbox"
										checked={visibleColumns.includes(col.field)}
										onChange={() => toggleColumnVisibility(col.field)}
										className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
									/>
									<label className="ml-2 text-sm text-gray-700">{col.headerName}</label>
								</div>
							))}
						</div>
					)}
				</div>

				<div className="flex gap-4 items-center">
					<button
						className="flex gap-1 items-center justify-between px-2 py-1.5 border shadow-sm text-[12px] font-medium rounded-md"
						onClick={onBtnExport}
					>
						Export CSV <DownloadSVG />
					</button>
					<button
						className="flex gap-1 items-center justify-between px-2 py-1.5 border shadow-sm text-[12px] font-medium rounded-md"
						onClick={handleExportPDF}
					>
						Export PDF <DownloadSVG />
					</button>
					{/* Button to open the JSON Tree modal */}
					<button
						className="flex gap-1 items-center justify-between px-2 py-1.5 border shadow-sm text-[12px] font-medium rounded-md"
						onClick={() => setIsJsonTreeModalOpen(true)}
					>
						Tree View
					</button>
					<button
						className="flex gap-1 items-center justify-between px-2 py-1.5 border shadow-sm text-[12px] font-medium rounded-md"
						onClick={toggleSidebar}
					>
						Filter <FilterSVG />
					</button>
				</div>
			</div>

			<FilterSidebar
				isOpen={isSidebarOpen}
				toggleSidebar={toggleSidebar}
				setStartDateTime={setStartDateTime}
				setEndDateTime={setEndDateTime}
				setSelectedParentIntent={setSelectedParentIntent}
				setSelectedChildIntent={setSelectedChildIntent}
				parentIntents={parentIntents}
				childIntents={childIntents}
			/>

			<div className="flex mt-2 pt-2 border-t">
				<div className="flex-1">
					{loading ? (
						<div className="text-sm">Loading...</div>
					) : (
						<div className="ag-theme-alpine" style={{ height: '70vh' }}>
							<AgGridReact
								ref={gridRef}
								suppressExcelExport={true}
								pagination={true}
								paginationPageSize={20}
								defaultColDef={defaultColDef}
								rowData={gridResponse}
								columnDefs={colDefs.filter((col) => visibleColumns.includes(col.field))}
								rowSelection={'multiple'}
								editType={'fullRow'}
								pivotPanelShow={'always'}
								enableCellEditingOnBackspace={true}
							/>
						</div>
					)}
				</div>
			</div>

			{isModalOpen && editableRowData && (
				<EditModal
					key={editableRowData.id}
					isOpen={isModalOpen}
					onClose={handleModalClose}
					onSave={handleSave}
					initialData={editableRowData}
				/>
			)}

			<TranscriptModal
				isOpen={!!transcriptLink}
				onClose={() => setTranscriptLink(null)}
				transcriptLink={transcriptLink || ''}
			/>

			{isJsonTreeModalOpen && (
				<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
					<div className="bg-white p-4 rounded shadow-lg max-h-full overflow-auto w-full max-w-3xl">
						<div className="flex justify-between items-center mb-4 border-b border-gray-300 pb-2">
							<h2 className="text-lg font-semibold">Data View</h2>
							<div className="flex items-center gap-2">
								<button
									onClick={handleDownloadCSV}
									className="flex gap-1 items-center px-2 py-1 border shadow-sm text-[12px] font-medium rounded-md"
								>
									Download CSV
								</button>
								<button onClick={() => setIsJsonTreeModalOpen(false)} className="text-gray-800 font-bold">
									<XMarkIcon className="h-8 w-8" aria-hidden="true" />
								</button>
							</div>
						</div>

						<JsonTreeDisplay />
					</div>
				</div>
			)}
		</div>
	);
};

export default LLMResponseTool;
